import { memo, useMemo } from 'react'
import type { Divisao, Representante } from '../api'
import { SEM_MEDICAO, foiMedido } from '../cartao'
import { faiscasDaVela } from '../confete'
import { milhoes, valorCurto } from '../formato'
import { confetesDaFesta, REPRESENTANTES_NA_FESTA, type TipoDeFesta } from '../marcosMarca'
import { MEDALHA, nomesDoRepresentante } from '../ranking'
import { Confetes } from './Confetes'

/**
 * A comemoração de UMA MARCA: ela sai do card e ocupa a tela inteira.
 *
 * Pedido em 14/10, nos dois gatilhos da meta da própria marca:
 *
 * |                | metade (50 %)   | meta (100 %)          |
 * |----------------|-----------------|-----------------------|
 * | confete        | 80, coloridos   | 160, dourados         |
 * | velas jupiter  | não             | uma de cada lado do nome |
 * | tempo na tela  | 12 s            | 20 s                  |
 *
 * O cabeçalho é logo + nome + o valor que a marca tem AGORA, e embaixo vem o
 * top 15 de representantes em ordem, com ouro, prata e bronze no pódio.
 *
 * Três decisões que vêm da tela da comemoração geral e valem aqui:
 *
 *  1. **nada é consultado aqui.** O ranking chega por `props`, do mesmo estado
 *     que a visão de marca usa — a festa não pode depender de uma requisição no
 *     instante em que dispara;
 *  2. **o valor é o VIVO**, não o do momento do disparo: o snapshot continua
 *     chegando enquanto a tela está no ar, e o número tem de acompanhar;
 *  3. **lista vazia DIZ por que**, em vez de sumir. Em 13/10 o pódio da
 *     comemoração do sexto milhão saiu vazio (a página havia carregado antes do
 *     primeiro ranking) e a tela não dava pista nenhuma de que faltava algo.
 */

/** As duas velas da META, uma de cada lado do nome, jorrando para fora. */
function VelaJupiter({ lado }: { lado: 'esquerda' | 'direita' }) {
  const faiscas = useMemo(() => faiscasDaVela(lado), [lado])

  return (
    /* `aria-hidden`: é enfeite. O que a comemoração comunica está no texto. */
    <span className={`vela vela-${lado}`} aria-hidden="true">
      <span className="vela-haste" />
      <span className="vela-jorro">
        {faiscas.map(faisca => (
          <span
            key={faisca.id}
            className="faisca"
            style={{
              width: `${faisca.tamanho}px`,
              height: `${faisca.tamanho}px`,
              animationDelay: `${faisca.atrasoMs}ms`,
              animationDuration: `${faisca.duracaoMs}ms`,
              ['--angulo' as string]: `${faisca.angulo}deg`,
              ['--alcance' as string]: `${faisca.alcance}px`,
            }}
          />
        ))}
      </span>
    </span>
  )
}

/** Uma linha do top 15. Medalha nas três primeiras, número nas outras. */
function LinhaFestaBase({ representante }: { representante: Representante }) {
  const { principal } = nomesDoRepresentante(representante)
  const medalha = MEDALHA[representante.posicao]

  return (
    <div
      className={
        representante.posicao <= 3
          ? `festa-linha festa-podio festa-lugar-${representante.posicao}`
          : 'festa-linha'
      }
    >
      <span className="festa-posicao">
        {medalha ? (
          /* O `aria-label` existe porque emoji sem rótulo é lido como "imagem". */
          <span className="festa-medalha" role="img" aria-label={medalha.rotulo}>
            {medalha.emoji}
          </span>
        ) : (
          <span className="festa-numero">{representante.posicao}º</span>
        )}
      </span>
      {/* `title` com o nome inteiro: no telão o texto é recortado por largura, e
          quem chega perto da tela consegue ler o resto. */}
      <span className="festa-nome" title={principal}>{principal}</span>
      <span className="festa-valor-linha">{valorCurto(representante.valor)}</span>
    </div>
  )
}

const LinhaFesta = memo(
  LinhaFestaBase,
  (antes, depois) => antes.representante === depois.representante,
)

type Props = {
  divisao: Divisao
  tipo: TipoDeFesta
  representantes: Representante[]
}

function ComemoracaoMarcaBase({ divisao, tipo, representantes }: Props) {
  const naMeta = tipo === 'meta'
  /* O corte é defesa, não regra: o servidor já manda 15 (`VisaoMarca`), e um
     contrato que mude do outro lado não deve empurrar 40 linhas na tela. */
  const lista = useMemo(
    () => (Array.isArray(representantes) ? representantes : []).slice(0, REPRESENTANTES_NA_FESTA),
    [representantes],
  )

  return (
    /* `role="status"` e não `alert`: é uma comemoração, não um aviso de erro. */
    <div className={naMeta ? 'festa-marca na-meta-marca' : 'festa-marca na-metade-marca'} role="status">
      <Confetes quantidade={confetesDaFesta(tipo)} paleta={naMeta ? 'dourada' : 'colorida'} />

      <div className="festa-miolo">
        {/* Tudo EMPILHADO e centrado, não logo-à-esquerda-texto-à-direita: com
            o valor em 130 px de altura ao lado de um logo de 190 px, o conjunto
            fica com peso de um lado só e a tela parece torta no telão (medido na
            captura de 14/10). Aqui o eixo é um: logo + nome, valor, frase. */}
        <div className="festa-cabeca">
          <div className="festa-cabeca-linha">
            <div className="festa-logo">
              {/* `alt` é a SIGLA, não vazio: logo apagado do disco depois de
                  cadastrado devolve 404, e com `alt=""` o quadro fica em branco —
                  a festa perderia justo a identidade da marca que a ganhou. */}
              {divisao.logo ? <img src={divisao.logo} alt={divisao.sigla} /> : divisao.sigla}
            </div>
            <div className="festa-nome-marca">
              {naMeta ? <VelaJupiter lado="esquerda" /> : null}
              <span className="festa-nome-texto">{divisao.nome}</span>
              {naMeta ? <VelaJupiter lado="direita" /> : null}
            </div>
          </div>

          <div className="festa-valor">
            {foiMedido(divisao.vendido) ? valorCurto(divisao.vendido) : SEM_MEDICAO}
          </div>
          <div className="festa-frase">{naMeta ? 'META BATIDA!' : 'METADE DA META!'}</div>
          {/* A meta por escrito é o que dá sentido ao "batida": de longe a sala
              lê a frase antes de conferir o número. */}
          <div className="festa-meta-dita">
            {naMeta ? '100%' : '50%'} DE {milhoes(divisao.meta)}
          </div>
        </div>

        <div className="festa-ranking">
          <div className="festa-ranking-titulo">
            TOP {REPRESENTANTES_NA_FESTA} REPRESENTANTES — {divisao.nome.toUpperCase()}
          </div>
          {lista.length === 0 ? (
            <div className="festa-sem-ranking">O RANKING DESTA MARCA ENTRA NO PRÓXIMO CICLO</div>
          ) : (
            <div className="festa-lista">
              {lista.map(representante => (
                <LinhaFesta
                  /* A `key` é o CÓDIGO, não a posição: com a posição, uma troca
                     de 3º lugar reusaria o nó da linha anterior e a medalha
                     trocaria de cor na pessoa errada. */
                  key={representante.codigo ?? `posicao-${representante.posicao}`}
                  representante={representante}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* `memo` pela identidade do que de fato muda: o painel por baixo continua
   recebendo snapshot enquanto a festa está na tela, e sem isto as 160 peças de
   confete e as 24 faíscas seriam reconciliadas a cada chegada. */
export const ComemoracaoMarca = memo(
  ComemoracaoMarcaBase,
  (antes, depois) =>
    antes.tipo === depois.tipo
    && antes.divisao === depois.divisao
    && antes.representantes === depois.representantes,
)
